"use client";

import { useMemo } from "react";
import { useTranslations } from "next-intl";
import { type FieldError, useFormState } from "react-hook-form";
import {
	Alert,
	DataField,
	Stack,
	Text,
	Tooltip,
} from "@leabankab/component-library";
import { SUBMIT_ERROR_PROPS } from "@/components/form";
import { DetailList, SectionHeading } from "@/components/ui";
import { useRefinanceAllocation } from "@/features/refinance/hooks/use-refinance-allocation";
import { useRefinanceScenario } from "@/features/refinance/hooks/use-refinance-scenario";
import { useCurrency, useOfferIntegrityWarning } from "@/hooks";
import { formatRate } from "@/shared/amount";
import { loanMonthlyPaymentCalc } from "@/shared/calculations";
import { useMonthsToYears } from "@/shared/use-months-to-years";
import { RefinanceContext } from "../types";
import { resolveAllocationMarkers } from "../utils/allocation-markers";
import {
	type AllocationBarMarker,
	DebtAllocationBar,
} from "./debt-allocation-bar";

interface LoanOverviewProps {
	context: RefinanceContext;
}

/**
 * Loan overview that replaces the library `DetailedSummary`. Reads the live
 * `debts` field array from form context and shows one allocation bar spanning
 * the loan ceiling, plus the resulting loan figures.
 */
export const LoanOverview = ({ context }: LoanOverviewProps) => {
	const t = useTranslations("pages.RefinancePage");

	const { format } = useCurrency();
	const formatDuration = useMonthsToYears();

	const { offer } = context;

	useOfferIntegrityWarning(offer, "refinance", {
		appliedRefinance: context.appliedRefinance,
	});

	const allocation = useRefinanceAllocation(context);

	// The totals rule sits on the `debts` array, at `root` once a debt is registered.
	const { errors, submitCount } = useFormState({ name: "debts" });
	const debtsError = errors.debts as
		(Partial<FieldError> & { root?: FieldError }) | undefined;
	const totalsBlockSubmit =
		submitCount > 0 && !!(debtsError?.root ?? debtsError?.message);

	const { isSkippable, isTopUp } = useRefinanceScenario(context);

	const {
		internal,
		mandatory,
		voluntary,
		headroom,
		payout,
		collected,
		totalCapacity,
		maxLoanAmount,
		excess,
		baselineLoan,
		loanAmount,
		cashPaidOut,
		refinanced,
	} = allocation;

	const monthlyAmount = useMemo(
		() => loanMonthlyPaymentCalc(offer, loanAmount),
		[offer, loanAmount]
	);

	// The refinance is part of the loan from the start, so it is shown as the
	// figure it will settle at, marked until the customer's debts cover it.
	const redeemedDebts = refinanced - internal;
	const refinancedValue =
		collected < refinanced
			? t.rich("overview.pendingValue", {
					amount: format(redeemedDebts),
					muted: (chunks) => (
						<Text render={<span />} textStyle="body-sm" color="text.subtle">
							{chunks}
						</Text>
					),
				})
			: format(redeemedDebts);

	const cashPaidOutLabel = isTopUp ? (
		<Stack direction="row" className="items-center gap-0.5">
			<Text render={<span />} color="text.subtle">
				{t("overview.cashPaidOut")}
			</Text>
			<Tooltip
				ariaLabel={t("overview.cashPaidOutInfoLabel")}
				title={t("overview.cashPaidOutInfo")}
			/>
		</Stack>
	) : (
		t("overview.cashPaidOut")
	);

	// The focus follows the priority order, so the caption always names the zone
	// the customer's next krona lands in. The payout is never an invitation, so
	// it takes focus only once the customer has started converting it.
	const focus =
		mandatory.remaining > 0
			? "mandatory"
			: voluntary.remaining > 0
				? "voluntary"
				: headroom.remaining > 0
					? "headroom"
					: payout.filled > 0
						? "payout"
						: "complete";

	const isUpsellOnly =
		mandatory.capacity === 0 &&
		voluntary.capacity === 0 &&
		headroom.capacity > 0;

	const markers: AllocationBarMarker[] = useMemo(
		() =>
			resolveAllocationMarkers(allocation).map(({ id, offset, ratio }) => ({
				id,
				ratio,
				description: t(`overview.tooltips.${id}`, { amount: format(offset) }),
			})),
		[allocation, format, t]
	);

	// The caption steps aside whenever an alert below carries the message.
	const caption = () => {
		if (excess > 0 || payout.filled > 0 || headroom.filled > 0)
			return undefined;

		if (focus === "mandatory") {
			return t("overview.remaining", { amount: format(mandatory.remaining) });
		}

		if (focus === "voluntary") {
			return t("overview.voluntaryRemaining", {
				amount: format(voluntary.remaining),
			});
		}

		if (focus === "headroom") {
			return isUpsellOnly && collected === internal
				? t("overview.headroomInvitation", {
						amount: format(headroom.capacity),
					})
				: t("overview.headroomRemaining", {
						amount: format(headroom.remaining),
					});
		}

		return t("overview.complete");
	};

	return (
		<Stack gap={1}>
			<SectionHeading>{t("overview.heading")}</SectionHeading>
			{!isSkippable && totalCapacity > 0 && (
				<Stack className="rounded-xl border border-border-base p-2.5">
					<DebtAllocationBar
						label={t("overview.collected")}
						amount={format(collected)}
						valueText={t("overview.barValueText", {
							collected: format(collected),
							capacity: format(maxLoanAmount),
						})}
						caption={caption()}
						captionBlocksSubmit={totalsBlockSubmit && mandatory.remaining > 0}
						allocation={allocation}
						markers={markers}
						existingDescription={t("overview.tooltips.existingLoan", {
							amount: format(internal),
						})}
						mandatoryDescription={t("overview.tooltips.mandatory", {
							amount: format(mandatory.capacity),
						})}
						markerGroupLabel={t("overview.markerGroupLabel")}
						legendLabels={{
							existing: t("overview.zones.existingLoan"),
							mandatory: t("overview.zones.mandatory"),
							gathered: t("overview.zones.gathered"),
						}}
					/>
				</Stack>
			)}
			{headroom.filled > 0 && excess === 0 && (
				<Alert severity="info" role="status" size="sm">
					{t("overview.loanIncreased", {
						baseline: format(baselineLoan),
						amount: format(loanAmount),
					})}
				</Alert>
			)}
			{payout.filled > 0 && excess === 0 && (
				<Alert severity="warning" role="status" size="sm">
					{t("overview.payoutReduced", {
						amount: format(payout.filled),
						payout: format(cashPaidOut),
					})}
				</Alert>
			)}
			{excess > 0 && (
				<Alert
					{...(totalsBlockSubmit ? SUBMIT_ERROR_PROPS : {})}
					severity="warning"
					role="status"
					size="sm"
				>
					{t("overview.aboveCeiling", { amount: format(excess) })}
				</Alert>
			)}

			<DetailList>
				<DataField
					label={t("overview.loanAmount")}
					value={format(baselineLoan)}
				/>
				{headroom.filled > 0 && (
					<DataField
						label={t("overview.increasedLoanAmount")}
						value={format(loanAmount)}
					/>
				)}
				{isTopUp && (
					<DataField
						label={t("overview.existingLoan")}
						value={format(internal)}
					/>
				)}
				<DataField
					label={t(isTopUp ? "overview.sumOtherDebts" : "overview.sumDebts")}
					value={refinancedValue}
				/>
				<DataField label={cashPaidOutLabel} value={format(cashPaidOut)} />

				<DataField
					label={t("overview.interestRate")}
					value={formatRate(offer.interestRate)}
				/>
				<DataField
					label={t("overview.terms")}
					value={formatDuration(offer.terms)}
				/>
				<DataField
					label={t("overview.monthlyAmount")}
					value={format(monthlyAmount)}
					divider={false}
				/>
			</DetailList>
		</Stack>
	);
};
